import React, { useContext, useEffect, useRef, useState } from 'react'
import { css, FlattenSimpleInterpolation, useTheme } from 'styled-components'
import { useDispatch } from 'react-redux'

import globalNotification from 'live/services/global-notification'
import { DashboardContext } from 'live/context/dashboard'
import { WidgetContext } from 'live/context/widget'
import i18n from 'live/services/i18n'

import axios from 'axios'

import { ChannelsAxisLabels, UnitSpan } from 'plugin-assets/shared/widget/components/widget-axis'
import { DashboardAlertEmitter, DashboardAlertEventKind } from '../services/dashboard_alert_emitter'
import { WidgetPropertiesInfo, WidgetExpression, WidgetAssetInfo, WidgetContextTypes } from '../types'
import { SUPPORTED_CHARTS } from '../constants/axis'
import { AxisAlertInfo, AxisProps } from '../types/axis'
import { getWidgetInfoHelper } from './helpers/widgetInfoHelper'
import { getAssetInfo, getChannelInfo } from './helpers/assetInfoHelper'
import { overwriteXhr } from './helpers/widgetRequestHelper'
import { StyledAxis } from './styles/StyledAxis.style'
import { channelSelectStyle, getAxisStyle, getWidgetStylesState } from './styles/WidgetStyles'
import { storeDispatchType, useSelector } from '../store'
import { withReduxWrapper } from '../store/wrapper'
import { alertSliceActions } from '../store/slices/alertSlice'
import useSkipFirstEffect from 'plugin-alerts-dashboard-channels/common/hooks/useSkipFirstEffect'

const widgetRequests = new Map<number, LiveWidgetRequest>()

overwriteXhr(widgetRequests)

const WidgetAxisAlert = (props: AxisProps): JSX.Element => {
    const theme = useTheme().themeName as Theme
    const widgetCtxRef = useRef<WidgetContextTypes>(null)

    const dashboardContext = useContext(DashboardContext)
    const widgetContext = (widgetCtxRef.current = (useContext(WidgetContext) as unknown) as WidgetContextTypes)

    const [axisStyle, setAxisStyle] = useState<FlattenSimpleInterpolation>()

    const supportChart = SUPPORTED_CHARTS.includes(props?.widget?.type) && !!dashboardContext?.dashboard

    const { axisInfo, widgetCriticality, selectChannel } = useSelector(state => state.alert)
    const dispatch = useDispatch<storeDispatchType>()

    const axisKey = `${widgetContext.widget.id}-${props.axis?.source?.qualifier}`
    const info = axisInfo[axisKey]
    const criticalAxis = widgetCriticality[widgetContext.widget.id]

    const addStatus = React.useCallback(
        (status: AxisAlertInfo): void => {
            const { widget, setCss } = widgetCtxRef.current
            if (widget.id !== status.widgetId) return

            setTimeout(() => {
                setCss(getWidgetStylesState(criticalAxis, theme))
                setAxisStyle(getAxisStyle(status, theme))
            })
        },
        [criticalAxis, theme]
    )

    useEffect(() => {
        if (info?.criticality && !selectChannel) {
            addStatus(info)
        } else {
            widgetCtxRef.current.setCss(css``)
            setAxisStyle(css``)
        }
    }, [info, addStatus, selectChannel])

    useSkipFirstEffect(() => {
        if (supportChart) {
            const { setCss } = widgetCtxRef.current
            if (selectChannel) {
                setCss(channelSelectStyle)
                setAxisStyle(css``)
            }
        }
    }, [selectChannel, supportChart])

    // https://gitlab.intelie.com/intelie/live/-/blob/master/webapp/js/modules/dashboard/lib/store.ts#L770
    const handleClick = async (): Promise<void> => {
        if (!dashboardContext?.dashboard) return

        try {
            const axis = props.axis.source
            const widget = widgetContext.widget
            const widgetConfig = widget.jsonConfig // TODO check widget diff between props and context
            const requestPayload = widgetRequests.get(widget.id)

            const [channelInfo, channelIdx] = getChannelInfo(widgetConfig, axis)

            const expressions = await axios
                .post<WidgetExpression[]>('rest/widget-query', [requestPayload])
                .then(({ data }) => data)

            const expression = expressions[channelIdx]

            if (!expression) throw new Error('No expression')

            const assetInfo = await getAssetInfo(axis, channelInfo, requestPayload)

            const widgetInfo = getWidgetInfoHelper(dashboardContext.dashboard, widget, expression, requestPayload)

            DashboardAlertEmitter.emit(DashboardAlertEventKind.OPEN_ALERT_MANAGEMENT_DRAWER)
            DashboardAlertEmitter.emit<WidgetAssetInfo>(DashboardAlertEventKind.CHANNEL_ASSET_INFO, assetInfo)
            DashboardAlertEmitter.emit<WidgetPropertiesInfo>(DashboardAlertEventKind.WIDGET_INFO, widgetInfo)
            dispatch(alertSliceActions.setSelectChannel(false))
        } catch (err) {
            globalNotification.error(i18n('Could not select the channel'))
            console.error(err)
        }
    }

    if (props.renderItem) {
        return (
            <StyledAxis onClick={handleClick} isVertical={props.isVertical} supported={supportChart} css={axisStyle}>
                {props.renderItem?.(props)}
            </StyledAxis>
        )
    }

    // Pipes widget axis

    const uom = props.uom || props.axis.__uom
    const axis = {
        ...props.axis,
        componentLabel: props.axis.componentLabel || (
            <>
                {props.label} {uom ? <UnitSpan>{`(${uom})`}</UnitSpan> : ''}
            </>
        )
    }

    return (
        <StyledAxis onClick={handleClick} isVertical={props.isVertical} css={axisStyle}>
            <ChannelsAxisLabels {...props} lastEventMap={{}} warnings={[]} axis={axis} />
        </StyledAxis>
    )
}

export default withReduxWrapper(WidgetAxisAlert)
